import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';
import { unreadCount } from '../services/suggestions';

/**
 * AppLayout — the one shared shell for every signed-in page, mirroring the iOS
 * app's five-tab structure (ContentView's TabView): Shop · Groups · My Wishes ·
 * Purchases · Profile.
 *
 * Desktop (md+): a top bar with the same five destinations.
 * Mobile: a fixed BOTTOM tab bar with icons + labels — so the web app in
 * mobile Safari navigates like the native app.
 *
 * The Groups tab links to /dashboard with state {stay: true} so the dashboard
 * doesn't auto-bounce a single-group user straight into their group when they
 * explicitly tapped Groups. It also stays highlighted while inside /group/:id
 * (a group detail lives "inside" the Groups tab, exactly like iOS).
 */
const TABS = [
    { to: '/feed', label: 'Shop', match: ['/feed'], icon: SparkleIcon },
    { to: '/dashboard', label: 'Groups', match: ['/dashboard', '/group'], icon: UsersIcon, state: { stay: true } },
    { to: '/wishes', label: 'My Wishes', match: ['/wishes'], icon: GiftIcon },
    { to: '/purchases', label: 'Purchases', match: ['/purchases'], icon: BagIcon },
    { to: '/profile', label: 'Profile', match: ['/profile'], icon: ProfileIcon },
];

export default function AppLayout({ maxWidth = 'max-w-5xl', children }) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const isActive = (tab) => tab.match.some((m) => pathname === m || pathname.startsWith(m + '/'));

    // Unread "ideas suggested for you" count → a small pill on the My Wishes tab.
    // Net-new pattern (no tab had a badge before). Re-poll on every route change;
    // opening My Wishes marks its suggestions seen, so navigating away and back
    // reflects the cleared count. No websockets — simple pull, like the inbox.
    const [unread, setUnread] = useState(0);
    useEffect(() => {
        let cancelled = false;
        unreadCount()
            .then((res) => {
                if (!cancelled) setUnread(res?.count || 0);
            })
            .catch(() => {
                if (!cancelled) setUnread(0);
            });
        return () => {
            cancelled = true;
        };
    }, [pathname]);

    const badgeFor = (tab) => (tab.to === '/wishes' ? unread : 0);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar: full nav on md+, logo only on mobile. */}
            <header className="bg-white shadow-sm sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
                    <Link to="/feed" className="flex items-center gap-2.5">
                        <svg width="30" height="30" viewBox="0 0 512 512" aria-hidden="true">
                            <rect width="512" height="512" rx="120" fill="#0284c7" />
                            <path d="M256 96 C 276 214, 298 236, 416 256 C 298 276, 276 298, 256 416 C 236 298, 214 276, 96 256 C 214 236, 236 214, 256 96 Z" fill="#ffffff" />
                        </svg>
                        <span className="text-xl font-bold text-gray-900">Wishful</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-6">
                        {TABS.slice(0, 4).map((tab) => {
                            const badge = badgeFor(tab);
                            return (
                                <Link
                                    key={tab.to}
                                    to={tab.to}
                                    state={tab.state}
                                    className={`text-sm font-medium flex items-center gap-1.5 ${isActive(tab) ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                                >
                                    {tab.label}
                                    {badge > 0 && (
                                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-semibold text-white bg-blue-600 rounded-full">
                                            {badge > 9 ? '9+' : badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        <Link
                            to="/profile"
                            className={`flex items-center gap-2 ${isActive(TABS[4]) ? 'text-blue-600' : 'text-gray-700 hover:text-gray-900'}`}
                        >
                            <Avatar name={user?.name} url={user?.avatar_url} color={user?.avatar_color} seed={user?.id} size={30} />
                            <span className="text-sm font-medium">{user?.name}</span>
                        </Link>
                    </nav>
                </div>
            </header>

            {/* pb-24 keeps content clear of the mobile bottom bar. */}
            <main className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 pb-24 md:pb-8`}>{children}</main>

            {/* Bottom tab bar — mobile only, mirrors the iOS TabView. */}
            <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]">
                <div className="grid grid-cols-5">
                    {TABS.map((tab) => {
                        const active = isActive(tab);
                        const Icon = tab.icon;
                        const badge = badgeFor(tab);
                        return (
                            <Link
                                key={tab.to}
                                to={tab.to}
                                state={tab.state}
                                className={`flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-[11px] font-medium ${active ? 'text-blue-600' : 'text-gray-500'}`}
                            >
                                <span className="relative">
                                    <Icon />
                                    {badge > 0 && (
                                        <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-semibold text-white bg-blue-600 rounded-full">
                                            {badge > 9 ? '9+' : badge}
                                        </span>
                                    )}
                                </span>
                                {tab.label}
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}

// Inline icons (24px, stroke = currentColor) approximating the iOS tab
// symbols: sparkles, person.3, gift, bag, person.crop.circle.
const iconProps = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

function SparkleIcon() {
    return (
        <svg {...iconProps}>
            <path d="M12 3c.9 4.6 2.5 6.2 7 7-4.5.8-6.1 2.4-7 7-.9-4.6-2.5-6.2-7-7 4.5-.8 6.1-2.4 7-7Z" />
            <path d="M19 15.5c.4 1.9 1 2.6 3 3-2 .4-2.6 1.1-3 3-.4-1.9-1-2.6-3-3 2-.4 2.6-1.1 3-3Z" strokeWidth={1.4} />
        </svg>
    );
}

function UsersIcon() {
    return (
        <svg {...iconProps}>
            <circle cx="9" cy="8" r="3.2" />
            <path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" />
            <path d="M16 5.4a3.2 3.2 0 0 1 0 5.9M17.8 14.9c1.7.6 2.9 2 3.3 4.1" />
        </svg>
    );
}

function GiftIcon() {
    return (
        <svg {...iconProps}>
            <rect x="4" y="8.5" width="16" height="4" rx="1" />
            <path d="M6 12.5V19a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 18 19v-6.5M12 8.5v12" />
            <path d="M12 8.5c-3.5 0-4.8-1.4-4.8-2.8S8.5 3.5 9.6 3.5c1.6 0 2.4 2.3 2.4 5 0-2.7.8-5 2.4-5 1.1 0 2.4.8 2.4 2.2s-1.3 2.8-4.8 2.8Z" />
        </svg>
    );
}

function BagIcon() {
    return (
        <svg {...iconProps}>
            <path d="M5.5 8.5h13l-.9 10.6a2 2 0 0 1-2 1.9H8.4a2 2 0 0 1-2-1.9L5.5 8.5Z" />
            <path d="M9 10.5V7a3 3 0 0 1 6 0v3.5" />
        </svg>
    );
}

function ProfileIcon() {
    return (
        <svg {...iconProps}>
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="10" r="3" />
            <path d="M6.2 18.3c1.1-2.3 3.2-3.4 5.8-3.4s4.7 1.1 5.8 3.4" />
        </svg>
    );
}
